import { useState, useEffect, useCallback } from 'react';
import CryptoJS from 'crypto-js';
import useAuth from './auth/publicUsers/useAuth';
import { Key } from './auth/publicUsers/Key';

// har user ki apni list -> "favoriteDoctors_<userId>"
const KEY_PREFIX = 'favoriteDoctors_';
const EVENT = 'favorites-updated';

// doctor ka unique id
const getId = (item) =>
    item?.id ?? item?.userId ?? item?.applicationUserId ?? item?.email ?? item?.userName;

const storageKey = (userId) => `${KEY_PREFIX}${userId ?? 'guest'}`;

// ---- encrypt / decrypt helpers (auth jaisa hi pattern) ----
const encrypt = (val) => {
    try {
        return CryptoJS.AES.encrypt(JSON.stringify(val), Key).toString();
    } catch {
        return null;
    }
};

const decrypt = (cipher) => {
    try {
        if (!cipher) return [];
        const bytes = CryptoJS.AES.decrypt(cipher, Key);
        const decrypted = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
        return Array.isArray(decrypted) ? decrypted : [];
    } catch {
        return [];
    }
};

const read = (userId) => {
    try {
        const raw = localStorage.getItem(storageKey(userId));
        return decrypt(raw);
    } catch {
        return [];
    }
};

const write = (userId, list) => {
    try {
        const cipher = encrypt(list);
        if (cipher) localStorage.setItem(storageKey(userId), cipher);
    } catch {
        // storage full / private mode - ignore
    }
    window.dispatchEvent(new Event(EVENT));
};

export default function useFavorites() {
    const { authentication } = useAuth();
    const userId = authentication?.data?.id ?? null;

    const [favorites, setFavorites] = useState(() => read(userId));

    // user change (login/logout) ya storage change hone par sync
    useEffect(() => {
        setFavorites(read(userId));
        const sync = () => setFavorites(read(userId));
        window.addEventListener(EVENT, sync);       // same tab
        window.addEventListener('storage', sync);   // doosre tab
        return () => {
            window.removeEventListener(EVENT, sync);
            window.removeEventListener('storage', sync);
        };
    }, [userId]);

    const isFavorite = useCallback(
        (item) => {
            const id = getId(item);
            return favorites.some((f) => getId(f) === id);
        },
        [favorites]
    );

    const addFavorite = useCallback((item) => {
        const list = read(userId);
        const id = getId(item);
        if (!list.some((f) => getId(f) === id)) write(userId, [item, ...list]);
    }, [userId]);

    const removeFavorite = useCallback((item) => {
        const id = getId(item);
        write(userId, read(userId).filter((f) => getId(f) !== id));
    }, [userId]);

    const toggleFavorite = useCallback((item) => {
        const id = getId(item);
        const list = read(userId);
        if (list.some((f) => getId(f) === id)) {
            write(userId, list.filter((f) => getId(f) !== id));
        } else {
            write(userId, [...list, item]);
        }
    }, [userId]);

    const clearFavorites = useCallback(() => write(userId, []), [userId]);

    return {
        favorites,
        count: favorites.length,
        isFavorite,
        addFavorite,
        removeFavorite,
        toggleFavorite,
        clearFavorites,
    };
}